import * as React from "react";
import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const CONSENT_KEY = "ga_consent";

declare global {
  interface Window {
    __setGaConsent?: (granted: boolean) => void;
  }
}

const readConsent = (): string | null => {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
};

/**
 * GDPR (EU) / PECR (UK) cookie-consent banner.
 *
 * Analytics storage is denied by default (see Consent Mode v2 setup in
 * index.html); Google Analytics is only loaded once the visitor explicitly
 * accepts here. "Reject" is presented as prominently as "Accept", as required
 * by the UK ICO and EDPB guidance.
 */
export const ConsentBanner: React.FC = () => {
  // Only ask if no decision has been recorded yet.
  const [visible, setVisible] = useState<boolean>(false);
  const [policyOpen, setPolicyOpen] = useState<boolean>(false);

  useEffect(() => {
    setVisible(readConsent() === null);
  }, []);

  const decide = (granted: boolean) => {
    window.__setGaConsent?.(granted);
    setVisible(false);
  };

  if (!visible) {
    return null;
  }

  return (
    <>
      <Paper
        square
        aria-label="Cookie consent"
        aria-live="polite"
        elevation={8}
        role="dialog"
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.snackbar + 1,
          p: 2,
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            gap: 2,
          }}
        >
          <Typography sx={{ flexGrow: 1 }} variant="body2">
            We use Google Analytics cookies to understand how the site is used
            so we can improve it. These are optional and are only set if you
            accept. Your FIX messages are always parsed locally in your browser
            and are never sent anywhere.{" "}
            <Link
              color="inherit"
              component="button"
              data-testid="consentLearnMore"
              type="button"
              underline="always"
              onClick={() => setPolicyOpen(true)}
            >
              Learn more
            </Link>
            .
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ flexShrink: 0 }}
          >
            <Button
              fullWidth
              color="inherit"
              data-testid="consentReject"
              variant="outlined"
              onClick={() => decide(false)}
            >
              Reject
            </Button>
            <Button
              fullWidth
              data-testid="consentAccept"
              variant="contained"
              onClick={() => decide(true)}
            >
              Accept
            </Button>
          </Stack>
        </Box>
      </Paper>
      <Dialog
        fullWidth
        aria-labelledby="cookie-policy-title"
        maxWidth="sm"
        open={policyOpen}
        // The banner is pinned to the bottom of the viewport at a zIndex above
        // the default modal layer (see Paper above). Without lifting the dialog
        // past it, the banner punches through the dialog on short viewports
        // (e.g. iPhone SE) and hides the bottom of the notice and the Close
        // button. Stack the whole modal — backdrop included — above the banner.
        sx={{ zIndex: (theme) => theme.zIndex.snackbar + 2 }}
        onClose={() => setPolicyOpen(false)}
      >
        <DialogTitle id="cookie-policy-title">
          Privacy &amp; Cookie Notice
        </DialogTitle>
        <DialogContent dividers>
          <Alert
            icon={<LockOutlinedIcon fontSize="inherit" />}
            severity="info"
            sx={{ mb: 2 }}
            variant="outlined"
          >
            <Typography sx={{ fontWeight: 600 }} variant="body2">
              This site processes the FIX messages you paste entirely within
              your own browser. Message content is never transmitted to any
              server.
            </Typography>
          </Alert>
          <Typography gutterBottom sx={{ fontWeight: 700 }} variant="subtitle2">
            Analytics cookies
          </Typography>
          <Typography paragraph variant="body2">
            With your consent, we use Google Analytics to collect anonymised,
            aggregated statistics (such as page views and which features are
            used) so we can improve the tool. Google Analytics sets cookies in
            your browser for this purpose.
          </Typography>
          <Typography gutterBottom sx={{ fontWeight: 700 }} variant="subtitle2">
            Your choice
          </Typography>
          <Typography paragraph variant="body2">
            Under the EU GDPR and the UK Privacy and Electronic Communications
            Regulations (PECR), these analytics cookies are only set after you
            give consent. No analytics cookies are stored and no data is sent to
            Google until you select &ldquo;Accept&rdquo;. You can decline by
            selecting &ldquo;Reject&rdquo;, and the site will work exactly the
            same. To change your decision later, clear this site&rsquo;s data in
            your browser settings.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPolicyOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConsentBanner;
